import React, { useCallback, useEffect, useRef, useState } from 'react';
import axios from 'axios';
import './MarketStatusBadge.css';

const POLL_MS = 12000;
const STUCK_SHOW_AFTER = 4;
const STUCK_RESTART_AFTER = 10;
const RESTART_COOLDOWN_MS = 5 * 60 * 1000;
const RESTART_COOLDOWN_KEY = 'bharatdesk_restart_cooldown';
const STATUS_TIMEOUT_MS = 20000;
const HEALTH_TIMEOUT_MS = 8000;

export default function MarketStatusBadge() {
  const [badge, setBadge] = useState({ label: '…', status: 'closed', istTime: '' });
  const [failCount, setFailCount] = useState(0);
  const stuckCountRef = useRef(0);
  const restartingRef = useRef(false);

  const triggerAutoRestart = useCallback(async (reason) => {
    if (restartingRef.current) return;
    const last = Number(sessionStorage.getItem(RESTART_COOLDOWN_KEY) || 0);
    if (Date.now() - last < RESTART_COOLDOWN_MS) return;

    restartingRef.current = true;
    sessionStorage.setItem(RESTART_COOLDOWN_KEY, String(Date.now()));
    stuckCountRef.current = 0;
    setBadge({
      label: 'RESTARTING',
      status: 'stuck',
      istTime: '',
      stuckReason: reason || 'Auto-restarting server…',
      marketOpen: false
    });

    try {
      await axios.post('/api/system/restart', { reason: reason || 'STUCK badge auto-restart' }, { timeout: 12000 });
    } catch {
      /* backend may already be down — watchdog / restart-backend.ps1 handles it */
    }

    setTimeout(() => {
      restartingRef.current = false;
      window.location.reload();
    }, 16000);
  }, []);

  const handleStuck = useCallback((reason, opts = {}) => {
    const allowRestart = opts.allowRestart !== false;
    stuckCountRef.current += 1;
    const n = stuckCountRef.current;

    if (n < STUCK_SHOW_AFTER) {
      setFailCount(n);
      setBadge((prev) => ({
        ...prev,
        stuckReason: `${reason} (${n}/${STUCK_SHOW_AFTER})`
      }));
      return;
    }

    setFailCount(n);
    setBadge({
      label: 'STUCK',
      status: 'stuck',
      istTime: '',
      stuckReason: reason,
      marketOpen: false
    });
    if (allowRestart && n >= STUCK_RESTART_AFTER) {
      triggerAutoRestart(reason);
    }
  }, [triggerAutoRestart]);

  const poll = useCallback(async () => {
    if (restartingRef.current) return;
    try {
      const { data } = await axios.get('/api/system/status', { timeout: STATUS_TIMEOUT_MS });
      setFailCount(0);
      stuckCountRef.current = 0;

      if (data.booting || data.label === 'BOOTING' || data.status === 'booting') {
        setBadge({
          label: 'BOOTING',
          status: 'booting',
          istTime: data.istTime || '',
          stuckReason: null,
          marketOpen: data.marketOpen === true
        });
        return;
      }

      if (data.status === 'stuck' || data.stuck) {
        handleStuck(data.stuckReason || 'System stuck', { allowRestart: true });
        return;
      }

      setBadge({
        label: data.label || 'CLOSED',
        status: data.status || 'closed',
        istTime: data.istTime || '',
        stuckReason: null,
        marketOpen: data.marketOpen === true
      });
    } catch (e) {
      try {
        const { data: health } = await axios.get('/api/health', { timeout: HEALTH_TIMEOUT_MS });
        if (health?.ok) {
          stuckCountRef.current = 0;
          setFailCount(0);
          setBadge({
            label: health.marketOpen === false ? 'CLOSED' : 'OPEN',
            status: health.marketOpen === false ? 'closed' : 'open',
            istTime: health.istTime || '',
            stuckReason: null,
            marketOpen: health.marketOpen !== false
          });
          return;
        }
      } catch {
        /* full offline */
      }
      const reason = e.code === 'ECONNABORTED'
        ? 'Server timeout'
        : 'Cannot reach server';
      // Do not auto-restart on reachability blips — that kills a recovering server in a loop.
      handleStuck(reason, { allowRestart: false });
    }
  }, [handleStuck]);

  useEffect(() => {
    poll();
    const timer = setInterval(poll, POLL_MS);
    return () => clearInterval(timer);
  }, [poll]);

  const title = badge.stuckReason
    ? `${badge.stuckReason}${badge.label === 'STUCK' ? ' · watchdog recovering' : ''}`
    : badge.istTime
      ? `IST ${badge.istTime}${failCount ? ` · poll fail ${failCount}` : ''}`
      : 'Market status';

  return (
    <span
      className={`market-status-badge market-status-${badge.status}${badge.label === 'RESTARTING' ? ' market-status-restarting' : ''}`}
      title={title}
      aria-live="polite"
      aria-label={`Market ${badge.label}`}
    >
      <span className="market-status-dot" aria-hidden />
      {badge.label}
    </span>
  );
}
